import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  PIN_CATEGORY, PIN_STATUS, PIN_PRIORITY,
  formatDateShort, formatRelative, isOverdue, ROLE_LABEL,
} from "@/lib/utils";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { Filter, FileWarning, AlertOctagon } from "lucide-react";

export default function IssuesDashboard() {
  const [pins, setPins] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    project_id: "",
    status: "",
    category: "",
    assigned_to: "",
    overdue: false,
  });

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/pins", { params }).then((r) => setPins(r.data));
  }, [filters]);

  const totals = pins.reduce((acc, p) => {
    if (["open", "assigned", "escalated", "in_progress", "pending_verification"].includes(p.status)) acc.open++;
    if (p.category === "safety_concern") acc.safety++;
    if (["high", "critical"].includes(p.priority)) acc.high++;
    if (isOverdue(p.due_date, p.status)) acc.overdue++;
    return acc;
  }, { open: 0, safety: 0, high: 0, overdue: 0 });

  const set = (k, v) => setFilters({ ...filters, [k]: v });

  return (
    <div className="p-8 max-w-[1700px] mx-auto" data-testid="issues-page">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-text-3)] mb-2">
          Field Issues
        </div>
        <h1 className="font-display text-4xl text-[var(--fop-text)]">Every issue on the drawings</h1>
        <p className="text-[var(--fop-text-2)] mt-2">Field issues, RFIs, safety concerns, inspections, and corrective actions — all anchored to sheets.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Open" value={totals.open} color="var(--st-open)" icon={FileWarning} />
        <Stat label="Safety" value={totals.safety} color="var(--st-escalated)" icon={AlertOctagon} />
        <Stat label="High Priority" value={totals.high} color="#fb923c" />
        <Stat label="Overdue" value={totals.overdue} color="var(--st-escalated)" />
      </div>

      {/* Filters */}
      <div className="fop-surface-2 rounded-lg p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="issues-filters">
        <Filter size={14} className="text-[var(--fop-text-3)]" />
        <select value={filters.project_id} onChange={(e) => set("project_id", e.target.value)} className="text-xs py-1.5 w-56">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => set("status", e.target.value)} className="text-xs py-1.5 w-40">
          <option value="">Any status</option>
          {Object.entries(PIN_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => set("category", e.target.value)} className="text-xs py-1.5 w-44">
          <option value="">Any category</option>
          {Object.entries(PIN_CATEGORY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} className="text-xs py-1.5 w-48">
          <option value="">Any assignee</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <label className="ml-auto flex items-center gap-2 text-xs text-[var(--fop-text-2)] cursor-pointer">
          <input type="checkbox" checked={filters.overdue} onChange={(e) => set("overdue", e.target.checked)} className="w-auto" data-testid="issues-overdue-only" />
          Overdue only
        </label>
        <button className="btn btn-ghost text-xs" onClick={() => setFilters({ project_id: "", status: "", category: "", assigned_to: "", overdue: false })} data-testid="issues-clear">
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="fop-surface-2 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Project</th>
              <th className="px-3 py-2">Assignee</th>
              <th className="px-3 py-2">Due</th>
              <th className="px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {pins.length === 0 && (
              <tr><td colSpan="8" className="text-center text-[var(--fop-text-3)] py-10">No issues match filters.</td></tr>
            )}
            {pins.map((p) => {
              const proj = projects.find((x) => x.id === p.project_id);
              const user = users.find((u) => u.id === p.assigned_to);
              const overdue = isOverdue(p.due_date, p.status);
              return (
                <tr key={p.id} className="border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface)]" data-testid={`issue-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/sheet/${p.sheet_id}`} className="text-[var(--fop-text)] hover:text-[var(--fop-primary)] font-medium">
                      {p.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="pill"
                      style={{ color: PIN_CATEGORY[p.category]?.color, borderColor: `${PIN_CATEGORY[p.category]?.color}50`, background: `${PIN_CATEGORY[p.category]?.color}15` }}
                    >
                      {PIN_CATEGORY[p.category]?.label}
                    </span>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2"><PriorityBadge priority={p.priority} /></td>
                  <td className="px-3 py-2 text-xs text-[var(--fop-text-2)]">{proj?.number || ""}</td>
                  <td className="px-3 py-2 text-xs text-[var(--fop-text-2)]">{user?.name || <span className="text-[var(--fop-text-4)]">—</span>}</td>
                  <td className="px-3 py-2 text-xs font-mono" style={{ color: overdue ? "var(--st-escalated)" : "var(--fop-text-3)" }}>
                    {p.due_date ? formatDateShort(p.due_date) : "—"}
                    {overdue && <span className="ml-1">⚠</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-[var(--fop-text-3)] uppercase tracking-wider">{formatRelative(p.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div className="fop-surface-2 rounded-lg p-4 flex items-center gap-3">
      {Icon && <Icon size={20} style={{ color }} />}
      <div>
        <div className="font-mono font-bold text-3xl leading-none" style={{ color }}>{value}</div>
        <div className="text-[10px] uppercase font-mono tracking-wider text-[var(--fop-text-3)] mt-1.5">{label}</div>
      </div>
    </div>
  );
}
